import React, { useState } from 'react';
import type {
  CdAdCopy,
  CdBlueprintItem,
  CdDossierHeader,
  CdDossierTabId,
  CdFocusBar,
  CdLabeledStat,
  CdRankedKeyword,
  CdSwotCard,
  CdSwotKind,
  CdTapeRow,
  CdThemeShare,
  CdTone,
} from '../../growth-competitor-dossier/types';
import {
  CD_AD_COPY,
  CD_ADS_STRATEGY,
  CD_BACKLINK_ASSETS,
  CD_BLUEPRINT_ITEMS,
  CD_BOOKING_MECHANICS,
  CD_CLINICAL_PROOF,
  CD_COMPLAINT_THEMES,
  CD_DOSSIER_CRAWL_META,
  CD_DOSSIER_HEADER,
  CD_FOCUS_BARS,
  CD_LOCAL_HASHTAGS,
  CD_LOCAL_VISIBILITY,
  CD_PRAISE_THEMES,
  CD_RANKED_KEYWORDS,
  CD_SEO_STRATEGY,
  CD_SOCIAL_METRICS,
  CD_SOCIAL_NETWORKS,
  CD_SWOT_CARDS,
  CD_TAPE_ROWS,
  CD_THEME_BREAKDOWN,
} from '../../growth-competitor-dossier/mockData';
import {
  CD_DOSSIER_TABS,
  GROWTH_COMPETITOR_DOSSIER_META,
} from '../../growth-competitor-dossier/config';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { Segmented } from '../Segmented/Segmented';
import './growth-competitor-dossier.css';

const TONE: Record<CdTone, { accent: string; tint: string }> = {
  success: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  gold: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  terra: {
    accent: 'var(--terracotta)',
    tint: 'color-mix(in srgb, var(--terracotta) 12%, transparent)',
  },
  info: { accent: 'var(--info)', tint: 'var(--info-tint)' },
  neutral: { accent: 'var(--ink-3)', tint: 'var(--surface-mute)' },
};

const SWOT_TONE: Record<CdSwotKind, CdTone> = {
  strength: 'success',
  weakness: 'terra',
  opportunity: 'info',
  threat: 'gold',
};

const eyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--ink-3)]';

const sectionLabelClass =
  'text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--ink-3)]';

function CdCard({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

function CdSectionHead({ icon, title }: { icon: IconKind; title: string }) {
  return (
    <div className="mb-4 flex items-center gap-2">
      <Icon kind={icon} size={16} stroke={1.9} className="text-[var(--ink-3)]" />
      <h3 className="m-0 text-[15px] font-semibold text-[var(--ink-0)]">{title}</h3>
    </div>
  );
}

function CdMiniMetric({ stat }: { stat: CdLabeledStat }) {
  return (
    <div className="flex-1 rounded-[var(--r-md)] bg-[var(--surface-mute)] px-4 py-3">
      <div className={eyebrowClass}>{stat.label}</div>
      <div className="mt-1.5 font-[var(--font-display)] text-[18px] font-bold tracking-[-0.02em] text-[var(--ink-0)]">
        {stat.value}
      </div>
    </div>
  );
}

function CdThreatRing({ score }: { score: number }) {
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, score));
  const offset = circumference - (clamped / 100) * circumference;
  const accent =
    score >= 70 ? 'var(--terracotta)' : score >= 40 ? 'var(--gold)' : 'var(--success)';

  return (
    <div className="relative h-[88px] w-[88px]">
      <svg width="88" height="88" viewBox="0 0 88 88" aria-hidden="true">
        <circle cx="44" cy="44" r={radius} fill="none" stroke="var(--surface-mute)" strokeWidth="5" />
        <circle
          cx="44"
          cy="44"
          r={radius}
          fill="none"
          stroke={accent}
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform="rotate(-90 44 44)"
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-[var(--font-display)] text-[28px] font-bold text-[var(--ink-0)]">
        {score}
      </span>
    </div>
  );
}

function CdThemeChip({ theme, tone }: { theme: string; tone: CdTone }) {
  const t = TONE[tone];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-[var(--r-pill)] px-2.5 py-1 text-[12px] font-semibold"
      style={{ background: t.tint, color: t.accent }}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {theme}
    </span>
  );
}

function CdAiStrategy({ points }: { points: readonly string[] }) {
  return (
    <div
      className="rounded-[var(--r-xl)] px-6 py-6 text-white"
      style={{ background: 'var(--grad-ink)' }}
    >
      <div className="flex items-center gap-2">
        <Icon kind="sparkle" size={16} stroke={2} className="text-white" />
        <span className="text-[11px] font-bold uppercase tracking-[0.1em]">
          AI Strategy Analysis
        </span>
      </div>
      <ul className="mt-3.5 list-disc space-y-2.5 pl-5">
        {points.map((point) => (
          <li
            key={point}
            className="text-[13px] leading-[1.6] text-white/90"
          >
            {point}
          </li>
        ))}
      </ul>
    </div>
  );
}

function CdTapeRowItem({ row, last }: { row: CdTapeRow; last: boolean }) {
  return (
    <div
      className={[
        'cd-tape-row grid items-center py-[13px]',
        last ? '' : 'border-b border-[var(--cream-2)]',
      ].join(' ')}
      style={{ gridTemplateColumns: '1fr 220px 1fr' }}
    >
      <div className="text-center">
        <span
          className="inline-flex rounded-[var(--r-pill)] px-4 py-1 text-[14px] font-semibold"
          style={{
            background: row.competitorWinning ? 'transparent' : 'var(--success-tint)',
            color: row.competitorWinning ? 'var(--ink-3)' : 'var(--success)',
          }}
        >
          {row.yourClinic}
        </span>
      </div>
      <div className="cd-tape-metric text-center">
        <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--ink-4)]">
          {row.metric}
        </span>
      </div>
      <div className="text-center">
        <span
          className="inline-flex rounded-[var(--r-pill)] px-4 py-1 text-[14px] font-semibold"
          style={{
            background: row.competitorWinning ? 'var(--success-tint)' : 'transparent',
            color: row.competitorWinning ? 'var(--success)' : 'var(--ink-3)',
          }}
        >
          {row.competitor}
        </span>
      </div>
    </div>
  );
}

function CdSwotCardItem({ card }: { card: CdSwotCard }) {
  const tone = TONE[SWOT_TONE[card.id]];
  return (
    <div
      className="rounded-[var(--r-xl)] px-[26px] py-[22px]"
      style={{
        background: tone.tint,
        border: `1px solid color-mix(in srgb, ${tone.accent} 30%, transparent)`,
      }}
    >
      <div
        className="text-[11px] font-bold uppercase tracking-[0.08em]"
        style={{ color: tone.accent }}
      >
        {card.label}
      </div>
      <div className="mt-3 font-[var(--font-display)] text-[16px] font-semibold text-[var(--ink-0)]">
        {card.title}
      </div>
      <div className="mt-1 text-[13px] italic text-[var(--ink-3)]">{card.subtitle}</div>
    </div>
  );
}

function CdFocusBarItem({ bar }: { bar: CdFocusBar }) {
  return (
    <div className="mb-4">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-[13px] font-medium text-[var(--ink-1)]">
          {bar.pct}% {bar.label}
        </span>
        <span className="font-[var(--font-mono)] text-[12px] text-[var(--ink-4)]">
          ${bar.cpc} avg CPC
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-[var(--r-pill)] bg-[var(--surface-mute)]">
        <div
          className="h-full rounded-[var(--r-pill)]"
          style={{ width: `${bar.pct}%`, background: TONE[bar.tone].accent }}
        />
      </div>
    </div>
  );
}

function CdAdCopyItem({ ad }: { ad: CdAdCopy }) {
  return (
    <div
      className="flex items-start justify-between gap-4 rounded-[var(--r-md)] bg-[var(--surface-mute)] px-5 py-4"
      style={{ borderLeft: `3px solid ${TONE[ad.tone].accent}` }}
    >
      <p className="flex-1 text-[14px] italic leading-[1.6] text-[var(--ink-1)]">{ad.text}</p>
      <span className="shrink-0 rounded-[var(--r-pill)] bg-[var(--cream-0)] px-3 py-1 text-[12px] font-medium text-[var(--ink-2)]">
        {ad.category}
      </span>
    </div>
  );
}

function CdBlueprintRow({ item }: { item: CdBlueprintItem }) {
  return (
    <div className="flex items-center gap-2.5">
      <Icon
        kind={item.active ? 'check-circ' : 'circle'}
        size={20}
        stroke={2}
        style={{ color: item.active ? 'var(--success)' : 'var(--ink-4)' }}
      />
      <span
        className="text-[13px]"
        style={{
          color: item.active ? 'var(--ink-1)' : 'var(--ink-4)',
          fontWeight: item.active ? 500 : 400,
        }}
      >
        {item.label}
      </span>
    </div>
  );
}

function CdRankedKeywordRow({ row, last }: { row: CdRankedKeyword; last: boolean }) {
  return (
    <div
      className={[
        'flex items-center justify-between py-3',
        last ? '' : 'border-b border-[var(--cream-2)]',
      ].join(' ')}
    >
      <span className="text-[14px] font-medium text-[var(--ink-1)]">{row.keyword}</span>
      <span className="rounded-[var(--r-pill)] bg-[var(--success-tint)] px-3 py-1 text-[12px] font-semibold text-[var(--success)]">
        {row.rank}
      </span>
    </div>
  );
}

function CdThemeRow({ row, last }: { row: CdThemeShare; last: boolean }) {
  return (
    <div
      className={[
        'flex items-center justify-between py-2.5',
        last ? '' : 'border-b border-[var(--cream-2)]',
      ].join(' ')}
    >
      <span className="text-[13px] text-[var(--ink-1)]">{row.theme}</span>
      <span className="font-[var(--font-display)] text-[15px] font-semibold text-[var(--success)]">
        {row.pct}%
      </span>
    </div>
  );
}

/* ── Tabs ── */

function CdOverviewTab() {
  return (
    <div className="space-y-7">
      {/* SWOT */}
      <div className="cd-swot-grid grid gap-3" style={{ gridTemplateColumns: '1fr 1fr' }}>
        {CD_SWOT_CARDS.map((card) => (
          <CdSwotCardItem key={card.id} card={card} />
        ))}
      </div>

      {/* Tale of the Tape */}
      <CdCard className="px-6 py-6">
        <h2 className="m-0 text-center font-[var(--font-display)] text-[22px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
          &ldquo;Tale of the Tape&rdquo;
        </h2>
        <p className="mb-5 mt-1 text-center text-[12px] font-medium text-[var(--ink-4)]">
          Green highlights the winning side for each metric
        </p>

        <div
          className="cd-tape-row grid border-b-2 border-[var(--cream-2)] pb-2.5"
          style={{ gridTemplateColumns: '1fr 220px 1fr' }}
        >
          <span className="text-center text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--success)]">
            Your Clinic
          </span>
          <span className="cd-tape-metric text-center text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--ink-4)]">
            Key Metric
          </span>
          <span className="text-center text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--ink-3)]">
            Competitor
          </span>
        </div>

        {CD_TAPE_ROWS.map((row, index) => (
          <CdTapeRowItem key={row.id} row={row} last={index === CD_TAPE_ROWS.length - 1} />
        ))}
      </CdCard>

      {/* Local Visibility + Review Sentiment */}
      <div className="cd-duo-grid grid gap-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <CdCard className="px-6 py-6">
          <CdSectionHead icon="pin" title="Local Visibility & Ratings" />
          <div className="mb-5 flex items-baseline gap-2">
            <span className="font-[var(--font-display)] text-[40px] font-semibold leading-none text-[var(--success)]">
              {CD_DOSSIER_HEADER.rating}
            </span>
            <Icon kind="star" size={20} stroke={1.8} className="text-[var(--gold)]" />
            <span className="ml-2 text-[13px] text-[var(--ink-4)]">
              {CD_DOSSIER_HEADER.reviews} total reviews
            </span>
          </div>
          <div className="flex gap-2.5">
            {CD_LOCAL_VISIBILITY.map((stat) => (
              <CdMiniMetric key={stat.label} stat={stat} />
            ))}
          </div>
        </CdCard>

        <CdCard className="px-6 py-6">
          <CdSectionHead icon="chat" title="Review Sentiment Mining" />
          <div className="mb-[18px]">
            <div className="mb-2.5 flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--success)]" />
              <span className={`${sectionLabelClass} !text-[var(--success)]`}>
                Top Praise Themes
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {CD_PRAISE_THEMES.map((theme) => (
                <CdThemeChip key={theme} theme={theme} tone="success" />
              ))}
            </div>
          </div>
          <div>
            <div className="mb-2.5 flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--terracotta)]" />
              <span className={`${sectionLabelClass} !text-[var(--terracotta)]`}>
                Top Complaint Themes
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {CD_COMPLAINT_THEMES.map((theme) => (
                <CdThemeChip key={theme} theme={theme} tone="terra" />
              ))}
            </div>
          </div>
        </CdCard>
      </div>

      {/* Booking / Clinical / Blueprint */}
      <div className="cd-trio-grid grid gap-4" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
        <CdCard className="px-6 py-6">
          <CdSectionHead icon="calendar" title="Booking Mechanics" />
          <div className="flex flex-col gap-2.5">
            {CD_BOOKING_MECHANICS.map((item) => (
              <CdMiniMetric key={item.label} stat={item} />
            ))}
          </div>
        </CdCard>

        <CdCard className="px-6 py-6">
          <CdSectionHead icon="clipboard" title="Clinical Proof" />
          <div className="flex flex-col gap-2.5">
            {CD_CLINICAL_PROOF.map((item) => (
              <CdMiniMetric key={item.label} stat={item} />
            ))}
          </div>
        </CdCard>

        <CdCard className="px-6 py-6">
          <CdSectionHead icon="pin" title="Blueprint" />
          <div className="mt-1 flex flex-col gap-3.5">
            {CD_BLUEPRINT_ITEMS.map((item) => (
              <CdBlueprintRow key={item.label} item={item} />
            ))}
          </div>
        </CdCard>
      </div>
    </div>
  );
}

function CdAdsTab() {
  return (
    <div className="space-y-7">
      <div className="cd-duo-grid grid gap-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <CdCard className="px-6 py-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Icon kind="target" size={16} stroke={1.9} className="text-[var(--ink-3)]" />
              <h3 className="m-0 text-[15px] font-semibold text-[var(--ink-0)]">
                Focus Distribution
              </h3>
            </div>
            <span className="rounded-[var(--r-pill)] bg-[var(--success-tint)] px-2.5 py-1 text-[12px] font-semibold text-[var(--success)]">
              12 Active Ads
            </span>
          </div>
          {CD_FOCUS_BARS.map((bar) => (
            <CdFocusBarItem key={bar.id} bar={bar} />
          ))}
        </CdCard>

        <CdAiStrategy points={CD_ADS_STRATEGY} />
      </div>

      <CdCard className="px-6 py-6">
        <CdSectionHead icon="broadcast" title="Ad Copy Swipe File" />
        <div className="flex flex-col gap-3">
          {CD_AD_COPY.map((ad) => (
            <CdAdCopyItem key={ad.id} ad={ad} />
          ))}
        </div>
      </CdCard>
    </div>
  );
}

function CdSeoTab() {
  return (
    <div className="space-y-7">
      <div className="cd-duo-grid grid gap-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <CdCard className="px-6 py-6">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div className="flex items-center gap-2">
              <Icon kind="lock" size={16} stroke={1.9} className="text-[var(--gold)]" />
              <h3 className="m-0 text-[15px] font-semibold text-[var(--ink-0)]">
                Top Ranked Keywords
              </h3>
            </div>
            <span className="text-[12px] text-[var(--ink-4)]">
              Backlink Velocity:{' '}
              <strong className="font-semibold text-[var(--success)]">+5 /mo</strong>
            </span>
          </div>

          {CD_RANKED_KEYWORDS.map((row, index) => (
            <CdRankedKeywordRow
              key={row.keyword}
              row={row}
              last={index === CD_RANKED_KEYWORDS.length - 1}
            />
          ))}

          <div className="mt-6">
            <div className={eyebrowClass}>Latest Backlink Assets</div>
            <div className="mt-2 flex flex-col gap-1.5">
              {CD_BACKLINK_ASSETS.map((url) => (
                <span
                  key={url}
                  className="font-[var(--font-mono)] text-[13px] font-medium text-[var(--success)]"
                >
                  {url}
                </span>
              ))}
            </div>
          </div>
        </CdCard>

        <CdAiStrategy points={CD_SEO_STRATEGY} />
      </div>
    </div>
  );
}

function CdSocialTab() {
  return (
    <div className="space-y-7">
      <div className="cd-duo-grid grid gap-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <CdCard className="px-6 py-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Icon kind="team" size={16} stroke={1.9} className="text-[var(--ink-3)]" />
              <h3 className="m-0 text-[15px] font-semibold text-[var(--ink-0)]">
                Organic Social Footprint
              </h3>
            </div>
            <div className="flex gap-1">
              {CD_SOCIAL_NETWORKS.map((network) => (
                <span
                  key={network.id}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-[var(--r-pill)] text-[11px] font-bold"
                  style={{
                    background: network.active ? 'var(--grad-ink)' : 'var(--surface-mute)',
                    color: network.active ? '#fff' : 'var(--ink-3)',
                  }}
                >
                  {network.label}
                </span>
              ))}
            </div>
          </div>
          <div className="mb-5 flex gap-2.5">
            {CD_SOCIAL_METRICS.map((stat) => (
              <CdMiniMetric key={stat.label} stat={stat} />
            ))}
          </div>
          <div className="rounded-[var(--r-md)] bg-[var(--surface-mute)] p-4">
            <div className={eyebrowClass}>Video Content Ratio</div>
            <div className="mt-2 flex h-7 overflow-hidden rounded-[var(--r-pill)]">
              <div
                className="flex items-center justify-center text-[11px] font-semibold text-white"
                style={{ width: '80%', background: 'var(--grad-ink)' }}
              >
                80% REELS
              </div>
              <div
                className="flex items-center justify-center text-[11px] font-semibold text-white"
                style={{ width: '20%', background: 'var(--info)' }}
              >
                20%
              </div>
            </div>
          </div>
        </CdCard>

        <CdCard className="px-6 py-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Icon kind="chat" size={16} stroke={1.9} className="text-[var(--ink-3)]" />
              <h3 className="m-0 text-[15px] font-semibold text-[var(--ink-0)]">
                Content Strategy & Themes
              </h3>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-[var(--r-pill)] bg-[var(--gold-tint)] px-2.5 py-1 text-[12px] font-semibold text-[var(--gold)]">
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
              High Sentiment Reach
            </span>
          </div>
          <div className="grid gap-6" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div>
              <div className={eyebrowClass}>Theme Breakdown</div>
              <div className="mt-1">
                {CD_THEME_BREAKDOWN.map((row, index) => (
                  <CdThemeRow
                    key={row.theme}
                    row={row}
                    last={index === CD_THEME_BREAKDOWN.length - 1}
                  />
                ))}
              </div>
            </div>
            <div>
              <div className={eyebrowClass}>Top Local Hashtags</div>
              <div className="mt-2.5 flex flex-col gap-2.5">
                {CD_LOCAL_HASHTAGS.map((tag) => (
                  <span key={tag} className="text-[13px] font-semibold text-[var(--success)]">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </CdCard>
      </div>
    </div>
  );
}

function CdTabContent({ tab }: { tab: CdDossierTabId }) {
  if (tab === 'ads') return <CdAdsTab />;
  if (tab === 'seo') return <CdSeoTab />;
  if (tab === 'social') return <CdSocialTab />;
  return <CdOverviewTab />;
}

export interface CompetitorDossierViewProps {
  header?: CdDossierHeader;
  loading?: boolean;
  onRecrawl?: () => void;
}

export function CompetitorDossierView({
  header: headerProp,
  loading = false,
  onRecrawl,
}: CompetitorDossierViewProps = {}) {
  const meta = GROWTH_COMPETITOR_DOSSIER_META;
  const header = headerProp ?? CD_DOSSIER_HEADER;
  const [tab, setTab] = useState<CdDossierTabId>('overview');

  return (
    <div className="growth-competitor-dossier-root relative mx-auto w-full max-w-[1320px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-[22px]">
        <div className="mb-2 flex items-center gap-2">
          <Icon kind="target" size={13} stroke={2} className="text-[var(--ai-3)]" />
          <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
            {meta.eyebrow}
          </span>
        </div>
        <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
          {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
        </h1>
        <p className="mt-2.5 max-w-[720px] text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
          {meta.description}
          {loading ? ' · Updating…' : ''}
        </p>
      </div>

      {/* Header card */}
      <CdCard className="mb-6 px-8 py-7">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-5">
            <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-[16px] border border-[color-mix(in_srgb,var(--success)_30%,transparent)] bg-[var(--success-tint)] font-[var(--font-display)] text-[20px] font-semibold text-[var(--success)]">
              {header.initials}
            </span>
            <div className="min-w-0">
              <h2 className="m-0 font-[var(--font-display)] text-[24px] font-semibold tracking-[-0.025em] text-[var(--ink-0)]">
                {header.name}
              </h2>
              <div className="mt-2 flex flex-wrap items-center gap-x-3.5 gap-y-2 text-[13px] text-[var(--ink-3)]">
                <span className="inline-flex items-center gap-1.5">
                  <Icon kind="pin" size={14} stroke={2} />
                  {header.address}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Icon kind="target" size={14} stroke={2} />
                  {header.distance}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Icon kind="star" size={14} stroke={1.8} className="text-[var(--gold)]" />
                  {header.rating}
                </span>
                <span className="text-[var(--ink-4)]">({header.reviews} reviews)</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {header.segments.map((segment) => (
                  <span
                    key={segment}
                    className="inline-flex rounded-[var(--r-pill)] bg-[var(--surface-mute)] px-2.5 py-1 text-[12px] font-medium text-[var(--ink-3)]"
                  >
                    {segment}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="flex shrink-0 flex-col items-center gap-1.5">
            <CdThreatRing score={header.threatScore} />
            <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--ink-3)]">
              Threat Score
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-[var(--r-pill)] bg-[var(--terracotta-tint)] px-2.5 py-1 text-[12px] font-semibold text-[var(--terracotta)]">
              <Icon kind="flame" size={13} stroke={2} />
              {header.moat}
            </span>
          </div>
        </div>
      </CdCard>

      <div className="mb-6">
        <Segmented
          options={CD_DOSSIER_TABS.map((t) => ({ value: t.id, label: t.label }))}
          value={tab}
          onChange={(value) => {
            setTab(value as CdDossierTabId);
          }}
        />
      </div>

      <CdTabContent tab={tab} />

      <div className="mt-7 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--cream-2)] pt-4">
        <span className="text-[12px] text-[var(--ink-4)]">
          Last crawled: {CD_DOSSIER_CRAWL_META.lastCrawled} · {CD_DOSSIER_CRAWL_META.nextCrawl}
        </span>
        <button
          type="button"
          onClick={onRecrawl}
          className="cursor-pointer rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-2 text-[13px] font-medium text-[var(--ink-2)] hover:bg-[var(--surface-mute)]"
        >
          Re-crawl Now
        </button>
      </div>
    </div>
  );
}
